import { ArrowUpCircle, ArrowDownCircle, RefreshCw, List, CreditCard } from 'lucide-react';
import { clsx } from 'clsx';
import { useTranslation } from 'react-i18next';
import { useCategoryTranslation } from '../../hooks/useCategoryTranslation';
import type { PersonalRecentTransaction } from '../../types';

interface PersonalRecentTransactionsListProps {
  transactions: PersonalRecentTransaction[];
  formatCurrency: (amount: number) => string;
  noCard?: boolean;
}

export default function PersonalRecentTransactionsList({
  transactions,
  formatCurrency,
  noCard = false,
}: PersonalRecentTransactionsListProps) {
  const { t, i18n } = useTranslation('dashboard');
  const { translateCategoryName } = useCategoryTranslation();
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(i18n.language, { day: 'numeric', month: 'short' });
  };

  if (transactions.length === 0) {
    const emptyContent = (
      <>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('recentTransactions.title')}</h3>
        <div className="flex flex-col items-center justify-center h-48 text-gray-500">
          <List className="w-12 h-12 mb-2 opacity-50" />
          <p className="text-sm">{t('recentTransactions.noTransactions')}</p>
        </div>
      </>
    );
    if (noCard) return emptyContent;
    return <div className="card h-full">{emptyContent}</div>;
  }

  const listContent = (
    <>
      <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('recentTransactions.title')}</h3>
      <div className="space-y-3 max-h-80 overflow-y-auto">
        {transactions.map((transaction) => (
          <div
            key={transaction.id}
            className="flex items-start p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <div
              className={clsx(
                'p-2 rounded-full flex-shrink-0 mt-0.5',
                transaction.type === 'income' ? 'bg-green-100' : 'bg-red-100'
              )}
            >
              {transaction.type === 'income' ? (
                <ArrowUpCircle className="w-5 h-5 text-green-600" />
              ) : (
                <ArrowDownCircle className="w-5 h-5 text-red-600" />
              )}
            </div>
            <div className="ml-3 flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2">
                <p className="font-medium text-gray-900 text-sm truncate">
                  {transaction.customLabel || (transaction.categoryId ? translateCategoryName(transaction.categoryId, transaction.categoryName, transaction.categoryIsDefault) : transaction.categoryName)}
                </p>
                <span
                  className={clsx(
                    'font-semibold text-sm whitespace-nowrap flex-shrink-0',
                    transaction.type === 'income' ? 'text-green-600' : 'text-red-600'
                  )}
                >
                  {transaction.type === 'income' ? '+' : '-'}
                  {formatCurrency(transaction.amount)}
                </span>
              </div>
              <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
                <span>{formatDate(transaction.date)}</span>
                {transaction.bankAccountName && (
                  <span
                    className="flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs"
                    style={{
                      backgroundColor: transaction.bankAccountColor
                        ? `${transaction.bankAccountColor}20`
                        : '#f3f4f6',
                      color: transaction.bankAccountColor || '#6b7280',
                    }}
                  >
                    <CreditCard className="w-3 h-3" />
                    {transaction.bankAccountName}
                  </span>
                )}
                {transaction.isRecurring && (
                  <span className="flex items-center gap-1 text-primary-600">
                    <RefreshCw className="w-3 h-3" />
                    {t('recentTransactionsDetails.recurring')}
                  </span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </>
  );

  if (noCard) return listContent;
  return <div className="card h-full">{listContent}</div>;
}
